import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

/**
 * Unified Session Manager for cross-platform authentication
 * Handles session storage across web and mobile platforms
 */
export class UnifiedSessionManager {
  static isCapacitor = Capacitor.isNativePlatform();

  /**
   * Set user session data
   * @param {Object} userData - User data to store
   * @param {number} expiry - Session expiry timestamp
   */
  static async setUserSession(userData, expiry = null) {
    try {
      const sessionData = {
        user: userData,
        timestamp: Date.now(),
        expiry: expiry || (Date.now() + (24 * 60 * 60 * 1000)) // 24 hours default
      };

      if (this.isCapacitor) {
        // Use Capacitor Preferences for mobile
        await Preferences.set({
          key: 'acctoo_user_session',
          value: JSON.stringify(sessionData)
        });
      } else {
        // Use localStorage for web
        localStorage.setItem('acctoo_user_session', JSON.stringify(sessionData));
      }

      // Also set individual user data for compatibility
      await this.setUserData(userData);
      
      console.log('✅ User session stored successfully');
      return true;
    } catch (error) {
      console.error('❌ Error storing user session:', error);
      return false;
    }
  }

  /**
   * Get user session data with validation and retry logic
   * @param {number} maxRetries - Maximum number of retry attempts
   * @returns {Object|null} User session data or null if not found/expired
   */
  static async getUserSession(maxRetries = 3) {
    let retryCount = 0;
    
    while (retryCount < maxRetries) {
      try {
        let sessionData = null;

        if (this.isCapacitor) {
          // Get from Capacitor Preferences with timeout
          const { value } = await Promise.race([
            Preferences.get({ key: 'acctoo_user_session' }),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Session read timeout')), 5000)
            )
          ]);
          sessionData = value ? JSON.parse(value) : null;
        } else {
          // Get from localStorage with validation
          const stored = localStorage.getItem('acctoo_user_session');
          if (stored) {
            try {
              sessionData = JSON.parse(stored);
            } catch (parseError) {
              console.warn('⚠️ Corrupted session data, clearing...');
              localStorage.removeItem('acctoo_user_session');
              throw new Error('Corrupted session data');
            }
          }
        }

        if (!sessionData) {
          return null;
        }

        // Validate session structure
        if (!this.validateSessionStructure(sessionData)) {
          console.warn('⚠️ Invalid session structure, clearing...');
          await this.clearUserSession();
          return null;
        }

        // Check if session is expired
        if (sessionData.expiry && Date.now() > sessionData.expiry) {
          console.log('⚠️ User session expired');
          await this.clearUserSession();
          return null;
        }

        // Check if session is too old (more than 7 days)
        const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days
        if (sessionData.timestamp && (Date.now() - sessionData.timestamp) > maxAge) {
          console.log('⚠️ User session too old, clearing...');
          await this.clearUserSession();
          return null;
        }

        return sessionData;
      } catch (error) {
        retryCount++;
        console.error(`❌ Error getting user session (attempt ${retryCount}/${maxRetries}):`, error);
        
        if (retryCount >= maxRetries) {
          // After max retries, clear potentially corrupted session
          await this.clearUserSession();
          return null;
        }
        
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 100));
      }
    }
    
    return null;
  }

  /**
   * Validate session data structure
   * @param {Object} sessionData - Session data to validate
   * @returns {boolean} True if valid, false otherwise
   */
  static validateSessionStructure(sessionData) {
    if (!sessionData || typeof sessionData !== 'object') {
      return false;
    }
    
    // Check required fields
    if (!sessionData.user || typeof sessionData.user !== 'object') {
      return false;
    }
    
    if (!sessionData.user.uid || typeof sessionData.user.uid !== 'string') {
      return false;
    }
    
    if (!sessionData.timestamp || typeof sessionData.timestamp !== 'number') {
      return false;
    }
    
    if (!sessionData.expiry || typeof sessionData.expiry !== 'number') {
      return false;
    }
    
    // Check timestamp is reasonable (not in the future, not too old)
    const now = Date.now();
    const oneDayAgo = now - (24 * 60 * 60 * 1000);
    const oneDayFromNow = now + (24 * 60 * 60 * 1000);
    
    if (sessionData.timestamp > oneDayFromNow || sessionData.timestamp < oneDayAgo) {
      return false;
    }
    
    return true;
  }

  /**
   * Set user data (compatibility method)
   * @param {Object} userData - User data to store
   */
  static async setUserData(userData) {
    try {
      if (this.isCapacitor) {
        await Preferences.set({
          key: 'acctoo_user',
          value: JSON.stringify(userData)
        });
      } else {
        localStorage.setItem('acctoo_user', JSON.stringify(userData));
      }
    } catch (error) {
      console.error('❌ Error storing user data:', error);
    }
  }

  /**
   * Get user data (compatibility method)
   * @returns {Object|null} User data or null
   */
  static async getUserData() {
    try {
      let userData = null;

      if (this.isCapacitor) {
        const { value } = await Preferences.get({ key: 'acctoo_user' });
        userData = value ? JSON.parse(value) : null;
      } else {
        const stored = localStorage.getItem('acctoo_user');
        userData = stored ? JSON.parse(stored) : null;
      }

      return userData;
    } catch (error) {
      console.error('❌ Error getting user data:', error);
      return null;
    }
  }

  /**
   * Set authentication token
   * @param {string} token - Auth token
   * @param {number} expiry - Token expiry timestamp
   */
  static async setAuthToken(token, expiry = null) {
    try {
      const tokenData = {
        token,
        timestamp: Date.now(),
        expiry: expiry || (Date.now() + (60 * 60 * 1000)) // 1 hour default
      };

      if (this.isCapacitor) {
        await Preferences.set({
          key: 'acctoo_auth_token',
          value: JSON.stringify(tokenData)
        });
      } else {
        localStorage.setItem('acctoo_auth_token', JSON.stringify(tokenData));
      }

      console.log('✅ Auth token stored successfully');
      return true;
    } catch (error) {
      console.error('❌ Error storing auth token:', error);
      return false;
    }
  }

  /**
   * Get authentication token
   * @returns {string|null} Auth token or null if not found/expired
   */
  static async getAuthToken() {
    try {
      let tokenData = null;

      if (this.isCapacitor) {
        const { value } = await Preferences.get({ key: 'acctoo_auth_token' });
        tokenData = value ? JSON.parse(value) : null;
      } else {
        const stored = localStorage.getItem('acctoo_auth_token');
        tokenData = stored ? JSON.parse(stored) : null;
      }

      if (!tokenData) {
        return null;
      }

      // Check if token is expired
      if (tokenData.expiry && Date.now() > tokenData.expiry) {
        console.log('⚠️ Auth token expired');
        await this.clearAuthToken();
        return null;
      }

      return tokenData.token;
    } catch (error) {
      console.error('❌ Error getting auth token:', error);
      return null;
    }
  }

  /**
   * Clear user session
   */
  static async clearUserSession() {
    try {
      if (this.isCapacitor) {
        await Preferences.remove({ key: 'acctoo_user_session' });
        await Preferences.remove({ key: 'acctoo_user' });
      } else {
        localStorage.removeItem('acctoo_user_session');
        localStorage.removeItem('acctoo_user');
      }

      console.log('✅ User session cleared');
      return true;
    } catch (error) {
      console.error('❌ Error clearing user session:', error);
      return false;
    }
  }

  /**
   * Clear authentication token
   */
  static async clearAuthToken() {
    try {
      if (this.isCapacitor) {
        await Preferences.remove({ key: 'acctoo_auth_token' });
      } else {
        localStorage.removeItem('acctoo_auth_token');
      }

      console.log('✅ Auth token cleared');
      return true;
    } catch (error) {
      console.error('❌ Error clearing auth token:', error);
      return false;
    }
  }

  /**
   * Clear all session data
   */
  static async clearAllSessionData() {
    try {
      await this.clearUserSession();
      await this.clearAuthToken();

      // Clear additional session data
      if (this.isCapacitor) {
        await Preferences.remove({ key: 'acctoo_session_expiry' });
        await Preferences.remove({ key: 'acctoo_device_info' });
      } else {
        localStorage.removeItem('acctoo_session_expiry');
        localStorage.removeItem('acctoo_device_info');
        localStorage.removeItem('acctoo_persistent_refreshed');
      }

      console.log('✅ All session data cleared');
      return true;
    } catch (error) {
      console.error('❌ Error clearing all session data:', error);
      return false;
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} True if user is authenticated
   */
  static async isAuthenticated() {
    try {
      const session = await this.getUserSession();
      const token = await this.getAuthToken();
      
      return !!(session && token);
    } catch (error) {
      console.error('❌ Error checking authentication status:', error);
      return false;
    }
  }

  /**
   * Get session status with validation and retry logic
   * @param {number} maxRetries - Maximum number of retry attempts
   * @returns {Object} Session status information
   */
  static async getSessionStatus(maxRetries = 3) {
    let retryCount = 0;
    
    while (retryCount < maxRetries) {
      try {
        const session = await this.getUserSession(maxRetries);
        const token = await this.getAuthToken();
        const isAuth = await this.isAuthenticated();

        // Validate session structure if it exists
        const isValidSession = !session || this.validateSessionStructure(session);

        return {
          isAuthenticated: isAuth && isValidSession,
          hasSession: !!session && isValidSession,
          hasToken: !!token,
          sessionExpiry: session?.expiry || null,
          tokenExpiry: token ? (await this.getAuthToken())?.expiry : null,
          platform: this.isCapacitor ? 'mobile' : 'web',
          isValid: isValidSession,
          error: null
        };
      } catch (error) {
        retryCount++;
        console.error(`❌ Error getting session status (attempt ${retryCount}/${maxRetries}):`, error);
        
        if (retryCount >= maxRetries) {
          // Clear potentially corrupted session after max retries
          await this.clearUserSession();
          return {
            isAuthenticated: false,
            hasSession: false,
            hasToken: false,
            sessionExpiry: null,
            tokenExpiry: null,
            platform: this.isCapacitor ? 'mobile' : 'web',
            isValid: false,
            error: error.message
          };
        }
        
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 100));
      }
    }
    
    return {
      isAuthenticated: false,
      hasSession: false,
      hasToken: false,
      sessionExpiry: null,
      tokenExpiry: null,
      platform: this.isCapacitor ? 'mobile' : 'web',
      isValid: false,
      error: 'Max retries exceeded'
    };
  }
}

export default UnifiedSessionManager;











